import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  Button,
  Image,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { MdCardGiftcard, MdOutlineShoppingBag } from "react-icons/md";
import { Link as RouterLink } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import SEO from "./SEO";
import { HEXACO_SAMPLE_REPORT_URLS } from "../data/hexacoData";
import { isHexacoWebPurchaseEnabled } from "../constants/hexaco";

import googlePlayIcon from "../images/Join_Now_GooglePlay.png";
import appStoreIcon from "../images/Download_AppStore.png";
import HexacoCarousel from "./HexacoCarousel";
import { getAssetLanguageKey } from "../i18n/language";

const Hexaco: React.FC = () => {
  const { t, i18n } = useTranslation();
  const freeReportModal = useDisclosure();
  const isZh = getAssetLanguageKey(
    i18n.resolvedLanguage || i18n.language,
  ) === "zh";
  const sampleReportUrl = isZh
    ? HEXACO_SAMPLE_REPORT_URLS.zh
    : HEXACO_SAMPLE_REPORT_URLS.en;

  return (
    <>
      <SEO
        title={t("hexaco.seo.title")}
        description={t("hexaco.seo.description")}
        keywords={t("hexaco.seo.keywords")}
        canonical="https://deepsoulai.com/hexaco"
      />

      {/* Hero Banner */}
      <Box
        bgGradient="linear(to-br, teal.50, blue.50, cyan.50)"
        pt={{ base: 10, md: 14 }}
        pb={{ base: 8, md: 10 }}
      >
        <Container maxW="1100px">
          <VStack spacing={3} textAlign="center">
            <Text fontSize={{ base: "3xl", md: "4xl" }}>🧭</Text>
            <Heading
              as="h1"
              size={{ base: "xl", md: "2xl" }}
              bgGradient="linear(to-r, teal.500, blue.500)"
              bgClip="text"
              fontWeight="extrabold"
            >
              {t("hexaco.title")}
            </Heading>
            <Text
              color="gray.600"
              maxW={{ base: "100%", md: "640px" }}
              fontSize={{ base: "sm", md: "md" }}
              lineHeight="tall"
              sx={{ textWrap: "pretty" }}
            >
              {t("hexaco.subtitle")}
            </Text>
          </VStack>
        </Container>
      </Box>

      <Box bg="gray.50" py={{ base: 8, md: 12 }}>
        <Container maxW="1100px">
          <VStack align="stretch" spacing={{ base: 10, md: 14 }}>
            <HexacoCarousel />

            {/* Sample report + purchase */}
            <Box
              bg="white"
              borderRadius="2xl"
              boxShadow="sm"
              border="1px solid"
              borderColor="teal.100"
              px={{ base: 6, md: 10 }}
              py={{ base: 8, md: 10 }}
              textAlign="center"
            >
              <VStack spacing={4}>
                <Text fontSize="3xl">📄</Text>
                <Heading size="md" color="gray.800">
                  {t("hexaco.sample.title")}
                </Heading>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color="gray.600"
                  maxW={{ base: "100%", md: "640px" }}
                  lineHeight="1.8"
                  sx={{ textWrap: "pretty" }}
                >
                  {t("hexaco.sample.desc")}
                </Text>
                <HStack
                  spacing={3}
                  justify="center"
                  flexWrap="wrap"
                  pt={1}
                >
                  <Button
                    as="a"
                    href={sampleReportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    colorScheme="teal"
                    borderRadius="full"
                    px={7}
                    rightIcon={<ExternalLinkIcon />}
                  >
                    {t("hexaco.sample.button")}
                  </Button>
                  {isHexacoWebPurchaseEnabled && (
                    <Button
                      as={RouterLink}
                      to="/hexaco/purchase"
                      colorScheme="blue"
                      borderRadius="full"
                      px={7}
                      rightIcon={
                        <Icon as={MdOutlineShoppingBag as React.ElementType} />
                      }
                    >
                      {t("hexaco.purchase.webCta.button")}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    colorScheme="teal"
                    borderWidth="2px"
                    borderRadius="full"
                    px={7}
                    bg="white"
                    rightIcon={
                      <Icon as={MdCardGiftcard as React.ElementType} />
                    }
                    _hover={{ bg: "teal.50" }}
                    onClick={freeReportModal.onOpen}
                  >
                    {t("hexaco.sample.freeButton")}
                  </Button>
                </HStack>
              </VStack>
            </Box>

            {/* CTA */}
            <Box
              bgGradient="linear(to-br, teal.500, blue.500)"
              borderRadius="2xl"
              px={{ base: 6, md: 10 }}
              py={{ base: 8, md: 10 }}
              textAlign="center"
            >
              <VStack spacing={4}>
                <Heading size="md" color="white">
                  {t("hexaco.cta.title")}
                </Heading>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color="whiteAlpha.900"
                  maxW="560px"
                  lineHeight="1.8"
                >
                  {t("hexaco.cta.desc")}
                </Text>
                <HStack spacing={4} justify="center" pt={2} flexWrap="wrap">
                  <Button
                    as="a"
                    href="https://play.google.com/store/apps/details?id=com.deepsoul.app.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="unstyled"
                    _hover={{
                      transform: "translateY(-3px) scale(1.04)",
                    }}
                    _active={{ transform: "scale(0.98)" }}
                  >
                    <Image
                      src={googlePlayIcon}
                      alt="Download on Google Play"
                      boxSize="150px"
                      maxW="150px"
                      maxH="50px"
                      objectFit="contain"
                      borderRadius="10px"
                    />
                  </Button>
                  <Button
                    as="a"
                    href="https://apps.apple.com/hk/app/deep-soul-spiritual-heritage/id6748895978?l=en-GB"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="unstyled"
                    _hover={{
                      transform: "translateY(-3px) scale(1.04)",
                    }}
                    _active={{ transform: "scale(0.98)" }}
                  >
                    <Image
                      src={appStoreIcon}
                      alt="Download on the App Store"
                      boxSize="150px"
                      maxW="150px"
                      maxH="50px"
                      objectFit="contain"
                      borderRadius="10px"
                    />
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>
      <Modal
        isOpen={freeReportModal.isOpen}
        onClose={freeReportModal.onClose}
        isCentered
        motionPreset="scale"
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(8px)" />
        <ModalContent
          borderRadius="2xl"
          mx={4}
          overflow="hidden"
          boxShadow="2xl"
          bg="white"
        >
          <Box
            bgGradient="linear(to-br, teal.400, cyan.400, blue.500)"
            pt={10}
            pb={8}
            px={6}
            textAlign="center"
            position="relative"
          >
            <ModalCloseButton color="white" top={3} right={3} />
            <Text
              position="absolute"
              top={4}
              left={5}
              fontSize="lg"
              aria-hidden
            >
              ✨
            </Text>
            <Text
              position="absolute"
              top={7}
              right={12}
              fontSize="md"
              aria-hidden
            >
              ✨
            </Text>
            <Flex
              mx="auto"
              boxSize="76px"
              borderRadius="full"
              bg="white"
              align="center"
              justify="center"
              boxShadow="lg"
              fontSize="3xl"
            >
              🎁
            </Flex>
            <Heading size="md" color="white" mt={4} fontWeight="extrabold">
              {t("hexaco.sample.freeModalTitle")}
            </Heading>
          </Box>
          <ModalBody py={7} px={{ base: 5, md: 8 }}>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.700"
              lineHeight="1.9"
              textAlign="center"
              sx={{ textWrap: "pretty" }}
            >
              <Trans
                i18nKey="hexaco.sample.appFreeHint"
                components={{
                  highlight: (
                    <Text
                      as="span"
                      fontWeight="bold"
                      color="teal.700"
                      bg="teal.100"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      whiteSpace="nowrap"
                    />
                  ),
                }}
              />
            </Text>
          </ModalBody>
          <ModalFooter justifyContent="center" pt={0} pb={7}>
            <Button
              onClick={freeReportModal.onClose}
              borderRadius="full"
              px={10}
              bgGradient="linear(to-r, teal.400, blue.500)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, teal.500, blue.600)",
                transform: "translateY(-1px)",
              }}
              _active={{ transform: "scale(0.98)" }}
            >
              {t("hexaco.sample.freeModalClose")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Hexaco;
